import { ChevronRight } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { getBookings } from '../../apis/booking';
import { Event } from '../../enums/event';
import useUserInfo from '../../hooks/useUserInfo';
import { recordEvent } from '../../utils/analytics';
import { isMobile } from '../../utils/common';
import { logout } from '../../utils/login';
import { Booking } from '../BookingCard/index.types';
import ErrorSection from '../Error/Section';
import LoggedOut from './LoggedOut';
import MobBookingCarousel from './MobBookingCarousel';
import NoBooking from './NoBooking';
import Skeleton from './Skeleton';
import WebBookingCarousel from './WebBookingCarousel';
import { BookingListProps } from './index.types';

const BookingList: FC<BookingListProps> = ({ setNoBooking, setPageLoading }) => {
    const { isUserLoggedIn, userInfo } = useUserInfo();
    const { fn: userName = 'ixigoer' } = userInfo;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState<Booking[]>([]);
    const [viewAll, setViewAll] = useState(false);
    const isMob = isMobile();

    const recentBookings = useMemo(() => data.slice(0, 10), [data]);

    const fetchBookings = useCallback(async () => {
        setLoading(true);

        try {
            const response = await getBookings();
            if (!response.length && setNoBooking) {
                setNoBooking(true);
                return;
            }
            setData(response);
        } catch (e) {
            if ((e as { message: string }).message?.includes('401')) logout();
            else setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    const onBookingCardClick = useCallback(
        (booking: Booking) => {
            setPageLoading?.(true);
            recordEvent(Event.HC_TRIP_OPEN, {
                bookingParams: booking,
            });
            window.location.href = `${window.location.origin}/support/booking/${booking.bookingId}?entryPoint=webview`;
        },
        [setPageLoading],
    );

    const renderViewAllButton = useCallback(() => {
        if (data.length <= 10) return null;

        return (
            <Button
                className={clsx(isMob ? 'justify-between mx-20 mb-5' : 'mx-30')}
                variant='flat'
                size='md'
                onClick={() => setViewAll(true)}
            >
                View all your bookings
                <ChevronRight />
            </Button>
        );
    }, [data, isMob]);

    useEffect(() => {
        if (!isUserLoggedIn) return;
        fetchBookings();
    }, [fetchBookings, isUserLoggedIn]);

    if (!isUserLoggedIn) {
        if (isMob) return <LoggedOut />;
        return null;
    }

    if (loading) return <Skeleton />;

    if (error) return <ErrorSection sectionName='recent bookings' onClickRetry={fetchBookings} />;

    if (!data.length) {
        if (isMob) return <NoBooking />;
        return null;
    }

    return (
        <div
            className={clsx(
                'flex flex-col pt-30',
                isMob ? 'pb-5 bg-white' : 'max-w-desktop-max mx-auto pb-10 bg-charcoal-40',
            )}
        >
            <div className='flex justify-between items-center'>
                <Typography variant='h4' fontWeight='medium' className={clsx(isMob ? 'px-20' : 'px-30')}>
                    Hi{userName ? ` ${userName}` : ''}, need help with your recent booking?
                </Typography>
                {!isMob && renderViewAllButton()}
            </div>
            {isMob ? (
                <MobBookingCarousel
                    bookings={data}
                    onClick={onBookingCardClick}
                    recentBookings={recentBookings}
                    setViewAll={setViewAll}
                    viewAll={viewAll}
                />
            ) : (
                <WebBookingCarousel
                    bookings={data}
                    onClick={onBookingCardClick}
                    recentBookings={recentBookings}
                    setViewAll={setViewAll}
                    viewAll={viewAll}
                />
            )}
            {isMob && renderViewAllButton()}
        </div>
    );
};

export default BookingList;
